import { Component, ElementRef, inject, OnInit, NgZone } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { Subject, takeUntil } from 'rxjs';
import { CustomizationService } from './services/customization/customization.service';
import { Customization } from './interfaces/customization.interface';
import { HelpersService } from './services/helpers/helpers.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: true,
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {
  public elementRef = inject(ElementRef);
  public customizationService = inject(CustomizationService);
  public helpersService = inject(HelpersService);
  public activatedRoute = inject(ActivatedRoute);
  public localStorageService = inject(LocalStorageService);
  private router = inject(Router);
  private zone = inject(NgZone);

  public destroy$: Subject<void> = new Subject<void>();
  public customization: Customization = {} as Customization;
  public isLoading: boolean = true; // to avoid flickering in the styles after fetching from api

  ngOnInit(): void {
    // Listen for deep links: spoqr://open?code=XXX&mode=sale
    this.initDeepLinkListener();

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const code = params['code'] || this.localStorageService.getItem('code');
        const mode = params['mode'] || this.localStorageService.getItem('mode');
        
        if (params['code']) {
          this.localStorageService.setItem('code', params['code']);
          this.localStorageService.deleteItem('userInfo');
        }
        if (params['mode']) {
          this.localStorageService.setItem('mode', params['mode']);
        } else if (!mode) {
          this.localStorageService.setItem('mode', 'catalog');
        }
        
        // Only load customization if we have a code and it's NOT a group code
        if (code) {
          const isGroupMode = !!localStorage.getItem('groupCode');
          if (isGroupMode) {
            // Group mode: use default customization, directory will apply business customization
            this.customizationService.setCustomizationVariables(
              this.customizationService.defaultCustomization,
            );
            const {
              isBorderRounded,
              heroBackgroundImage,
              heroImageColorOpacity,
              heroLogo,
              ...colors
            } = this.customizationService.defaultCustomization;
            const auxPrimaryTextColorRGB = this.helpersService.convertHexToRGB(colors.auxPrimaryTextColor);
            const cartProductCardPrimaryTextColorRGB = this.helpersService.convertHexToRGB(colors.cartProductCardPrimaryTextColor);
            const primaryColorRGB = this.helpersService.convertHexToRGB(colors.primaryColor);
            const secondaryColorRGB = this.helpersService.convertHexToRGB(colors.secondaryColor);
            this.helpersService.setAppCustomStyleVariables(
              { ...colors, primaryColorRGB, secondaryColorRGB, cartProductCardPrimaryTextColorRGB, auxPrimaryTextColorRGB, heroBackgroundImage, heroImageColorOpacity, heroLogo, primaryBorderRadius: '8px', secondaryBorderRadius: '8px' },
              this.elementRef,
            );
            this.isLoading = false;
          } else {
            this.customizationService
            .getCustomizationVariables()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (customization) => {
                let currentCustomization = customization;
                if (!customization)
                  currentCustomization =
                    this.customizationService.defaultCustomization;
                this.customizationService.setCustomizationVariables(
                  currentCustomization,
                );
                const {
                  isBorderRounded,
                  heroBackgroundImage,
                  heroImageColorOpacity,
                  heroLogo,
                  ...colors
                } = currentCustomization;
                const auxPrimaryTextColorRGB =
                  this.helpersService.convertHexToRGB(colors.auxPrimaryTextColor);
                const cartProductCardPrimaryTextColorRGB =
                  this.helpersService.convertHexToRGB(
                    colors.cartProductCardPrimaryTextColor,
                  );
                const primaryColorRGB = this.helpersService.convertHexToRGB(
                  colors.primaryColor,
                );
                const secondaryColorRGB = this.helpersService.convertHexToRGB(
                  colors.secondaryColor,
                );
                this.helpersService.setAppCustomStyleVariables(
                  {
                    ...colors,
                    primaryColorRGB,
                    secondaryColorRGB,
                    cartProductCardPrimaryTextColorRGB,
                    auxPrimaryTextColorRGB,
                    heroBackgroundImage,
                    heroImageColorOpacity,
                    heroLogo,
                    primaryBorderRadius: '8px',
                    secondaryBorderRadius: '8px',
                  },
                  this.elementRef,
                );
                this.isLoading = false;
              },
              error: () => {
                this.customizationService.setCustomizationVariables(
                  this.customizationService.defaultCustomization,
                );
                const {
                  isBorderRounded,
                  heroBackgroundImage,
                  heroImageColorOpacity,
                  heroLogo,
                  ...colors
                } = this.customizationService.defaultCustomization;
                const auxPrimaryTextColorRGB =
                  this.helpersService.convertHexToRGB(colors.auxPrimaryTextColor);
                const cartProductCardPrimaryTextColorRGB =
                  this.helpersService.convertHexToRGB(
                    colors.cartProductCardPrimaryTextColor,
                  );
                const primaryColorRGB = this.helpersService.convertHexToRGB(
                  colors.primaryColor,
                );
                const secondaryColorRGB = this.helpersService.convertHexToRGB(
                  colors.secondaryColor,
                );
                this.helpersService.setAppCustomStyleVariables(
                  {
                    ...colors,
                    primaryColorRGB,
                    secondaryColorRGB,
                    cartProductCardPrimaryTextColorRGB,
                    auxPrimaryTextColorRGB,
                    heroBackgroundImage,
                    heroImageColorOpacity,
                    heroLogo,
                    primaryBorderRadius: '8px',
                    secondaryBorderRadius: '8px',
                  },
                  this.elementRef,
                );
                this.isLoading = false;
              },
            });
          }
        } else {
          this.isLoading = false;
        }
      });
  }

  /**
   * Initialize deep link listener for Capacitor
   * Handles URLs like: spoqr://open?code=XXX&mode=sale
   */
  private initDeepLinkListener(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = new URL(event.url);
        const code = url.searchParams.get('code');
        const mode = url.searchParams.get('mode');

        if (code) {
          this.localStorageService.setItem('code', code);
          this.localStorageService.deleteItem('userInfo');
        }
        if (mode) {
          this.localStorageService.setItem('mode', mode);
        } else {
          this.localStorageService.setItem('mode', 'catalog');
        }

        // Navigate to intro with params
        this.router.navigate(['/client-info/intro'], {
          queryParams: { code, mode: mode || 'sale' }
        });
      });
    });
  }
}
